import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Button,
  TableBody,
  Typography,
  Box,
  IconButton,
  TextField,
  Grid,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Snackbar,
  Alert,
  TableContainer,
  TablePagination,
  InputAdornment,
  Chip,
  Checkbox,
  Popover,
  List,
  ListItem,
  ListItemButton,
  Divider,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const STATUS_OPTIONS = ['EN ESPERA PROGRAMACION DE PAGO', 'PROGRAMADO', 'PAGADO'];

const statusColor = (s) => {
  switch (s) {
    case 'EN ESPERA PROGRAMACION DE PAGO': return 'default';
    case 'PROGRAMADO': return 'warning';
    case 'PAGADO': return 'success';
    default: return 'default';
  }
};

// color opcional para estado de factura (puedes ajustar si gustas)
const docStatusColor = (_s) => 'default';

const PagosProv = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  // data
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ "EN ESPERA PROGRAMACION DE PAGO": 0, PROGRAMADO: 0, PAGADO: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // user & lookup
  const [usersData, setUsersData] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [userRfc, setUserRfc] = useState('');
  const [userPartner, setUserPartner] = useState('');

  // filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    rfcs: [],
    partners: [],
    fechaDesde: '',
    fechaHasta: '',
    statuses: []
  });

  // ui
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentFilter, setCurrentFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState('');
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('updated_at');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  // detalle
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const isProveedor = currentUser?.roles?.includes('Proveedor');

  // helpers mapping
  const getUserNameByPartner = (partnerId) => {
    if (!partnerId || !usersData.length) return '';
    const clean = String(partnerId).replace(/^0+/, '');
    const user = usersData.find(u => u.partner && String(u.partner).replace(/^0+/, '') === clean);
    return user ? user.nombre : '';
  };

  const getRfcByPartner = (partnerId) => {
    if (!partnerId || !usersData.length) return '';
    const clean = String(partnerId).replace(/^0+/, '');
    const user = usersData.find(u => u.partner && String(u.partner).replace(/^0+/, '') === clean);
    return user ? (user.rfc || user.username) : '';
  };

  const getUniqueRfcsInData = () => {
    if (isProveedor) return [userRfc];
    const rfcs = data.map(r => getRfcByPartner(r.Supplier)).filter(Boolean);
    return [...new Set(rfcs)];
  };

  const getUniquePartnersInData = () => {
    if (isProveedor) return [userPartner];
    return [...new Set(data.map(r => r.Supplier))];
  };

  // sort
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getComparableValue = (row, by) => {
    switch (by) {
      case 'doc':
        return `${row.CompanyCode}-${row.FiscalYear}-${row.SupplierInvoice}`;
      case 'rfc':
        return (getRfcByPartner(row.Supplier) || '').toLowerCase();
      case 'partner':
        return (row.Supplier || '').toString();
      case 'nombre':
        return (getUserNameByPartner(row.Supplier) || '').toLowerCase();
      case 'PostingDate':
        return row.PostingDate ? new Date(row.PostingDate).getTime() : 0;
      case 'ValueDate':
        return row.ValueDate ? new Date(row.ValueDate).getTime() : 0;
      case 'EstimatedDueDate':
        return row.EstimatedDueDate ? new Date(row.EstimatedDueDate).getTime() : 0;
      case 'updated_at':
        return row.updated_at ? new Date(row.updated_at).getTime() : 0;
      case 'InvoiceGrossAmount':
        return Number(row.InvoiceGrossAmount) || 0;
      case 'Status': // estado de pago
        return row.Status || '';
      case 'InvoiceDocStatus': // estado de factura
        return row.InvoiceDocStatus || '';
      default:
        return (row?.[by] ?? '').toString().toLowerCase();
    }
  };

  const descendingComparator = (a, b, by) => {
    const A = getComparableValue(a, by);
    const B = getComparableValue(b, by);
    if (B < A) return -1;
    if (B > A) return 1;
    return 0;
  };
  const getComparator = (ord, by) => ord === 'desc'
    ? (a, b) => descendingComparator(a, b, by)
    : (a, b) => -descendingComparator(a, b, by);

  // === SERVER FETCH ===
  const fetchList = useCallback(async () => {
    try {
      setLoading(true);

      const params = {
        page: page + 1,
        pageSize: rowsPerPage,
        from: filters.fechaDesde || undefined,
        to:   filters.fechaHasta || undefined,
        status: (filters.statuses || []).join(','),
        q: searchTerm || undefined,
        _ts: Date.now()
      };

      const { data: listResp } = await axios.get('https://facturas.verdevalle.com.mx/api/portal/invoices', {
        withCredentials: true,
        params
      });

      setData(listResp.rows || []);
      setCounts(listResp.counts || { "EN ESPERA PROGRAMACION DE PAGO": 0, PROGRAMADO: 0, PAGADO: 0 });
      setTotal(listResp.total || 0);
    } catch (e) {
      console.error('Error cargando facturas:', e);
      setError('Error al cargar datos de facturas.');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters.fechaDesde, filters.fechaHasta, filters.statuses, searchTerm]);

  // carga inicial: sesión + usuarios
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const userResp = await axios.get('https://facturas.verdevalle.com.mx/api/validate-session', { withCredentials: true });
        setCurrentUser(userResp.data.user);
        setUserRfc(userResp.data.user.rfc || userResp.data.user.username);

        const usersResp = await axios.get('https://facturas.verdevalle.com.mx/api/usuarios', { withCredentials: true });
        setUsersData(usersResp.data);
        const userRow =
          usersResp.data.find(u => u.id === userResp.data.user.id) ||
          usersResp.data.find(u => (u.rfc || u.username) === (userResp.data.user.rfc || userResp.data.user.username));
        setUserPartner(userRow?.partner || '');
      } catch (e) {
        console.error('Error sesión/usuarios:', e);
        setError('Error al cargar sesión o usuarios.');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // fetch al cambiar filtros/paginación
  useEffect(() => {
    const t = setTimeout(() => {
      fetchList();
    }, 250);
    return () => clearTimeout(t);
  }, [fetchList]);

  // popover filtros
  const open = Boolean(anchorEl);
  const popoverId = open ? 'filter-popover' : undefined;

  const handleFilterClick = (event, filterType) => {
    setCurrentFilter(filterType);
    setAnchorEl(event.currentTarget);
    setFilterSearch('');
  };
  const handleFilterClose = () => {
    setAnchorEl(null);
    setCurrentFilter(null);
    setFilterSearch('');
  };
  const handleToggleFilterItem = (value) => {
    const keyMap = { rfc: 'rfcs', partner: 'partners', status: 'statuses' };
    const key = keyMap[currentFilter];
    const cur = new Set(filters[key]);
    cur.has(value) ? cur.delete(value) : cur.add(value);
    const next = { ...filters, [key]: Array.from(cur) };
    setFilters(next);
    if (key === 'statuses') setPage(0);
  };
  const handleSelectAllFilterItems = () => {
    const keyMap = { rfc: 'rfcs', partner: 'partners', status: 'statuses' };
    const key = keyMap[currentFilter];

    let allValues = [];
    if (currentFilter === 'rfc') allValues = getUniqueRfcsInData();
    if (currentFilter === 'partner') allValues = getUniquePartnersInData().map(String);
    if (currentFilter === 'status') allValues = STATUS_OPTIONS;

    const next = {
      ...filters,
      [key]: filters[key].length === allValues.length ? [] : allValues
    };
    setFilters(next);
    if (key === 'statuses') setPage(0);
  };
  const getFilteredOptions = () => {
    if (currentFilter === 'rfc') {
      return getUniqueRfcsInData().filter(v => v.toLowerCase().includes(filterSearch.toLowerCase()));
    }
    if (currentFilter === 'partner') {
      return getUniquePartnersInData()
        .map(String)
        .filter(v =>
          v.toLowerCase().includes(filterSearch.toLowerCase()) ||
          getUserNameByPartner(v).toLowerCase().includes(filterSearch.toLowerCase())
        );
    }
    if (currentFilter === 'status') {
      return STATUS_OPTIONS.filter(s => s.toLowerCase().includes(filterSearch.toLowerCase()));
    }
    return [];
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters({
      rfcs: [],
      partners: [],
      fechaDesde: '',
      fechaHasta: '',
      statuses: []
    });
    setPage(0);
  };

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  // paginación
  const handleChangePage = (_e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // detalle
  const openDetail = async (row) => {
    try {
      setDetail(null);
      setDetailOpen(true);
      const resp = await axios.get(
        `https://facturas.verdevalle.com.mx/api/portal/invoices/${row.CompanyCode}/${row.FiscalYear}/${row.SupplierInvoice}`,
        { withCredentials: true }
      );
      setDetail(resp.data);
    } catch (e) {
      console.error(e);
      setDetail({ error: 'No se pudo cargar el detalle' });
    }
  };

  // Filtrado y orden local
  const rowsToShow = useMemo(() => {
    const filteredLocal = data.filter(r => {
      const matchesRfc = filters.rfcs.length
        ? filters.rfcs.includes(getRfcByPartner(r.Supplier))
        : true;

      const matchesPartner = filters.partners.length
        ? filters.partners.includes(String(r.Supplier))
        : true;

      const onlyMine = isProveedor ? getRfcByPartner(r.Supplier) === userRfc : true;

      return matchesRfc && matchesPartner && onlyMine;
    });

    const sorted = filteredLocal.sort(getComparator(order, orderBy));
    return sorted;
  }, [data, filters.rfcs, filters.partners, isProveedor, userRfc, order, orderBy]);

  return (
    <Box sx={{ p: isMobile ? 1 : 2, maxWidth: '1400px', m: '0 auto' }}>
      <Box sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        mb: 2,
        gap: 1
      }}>
        <Typography variant="h6" component="h1" sx={{ fontWeight: 'bold' }}>
          Estado de Facturas
        </Typography>

        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate('/')}
          startIcon={<ArrowBackIcon fontSize="small" />}
          sx={{ width: isMobile ? '100%' : 'auto', fontSize: '0.8rem' }}
        >
          Volver a factura
        </Button>
      </Box>

      {/* Indicadores por estado de pago */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        {STATUS_OPTIONS.map(s => (
          <Chip
            key={s}
            label={`${s}: ${counts[s] ?? 0}`}
            color={statusColor(s)}
            variant="outlined"
            size="small"
            sx={{ fontSize: '0.75rem' }}
          />
        ))}
      </Box>

      <Paper elevation={1} sx={{
        p: 2, mb: 1.5, borderRadius: 3, backgroundColor: '#fff',
        boxShadow: '0 10px 24px rgba(2, 6, 23, 0.06)', border: '1px solid #eef2f7'
      }}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={5} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Buscar (doc o referencia)"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <IconButton size="small" onClick={() => setSearchTerm('')} sx={{ mr: -1 }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ),
                sx: { height: '35px' }
              }}
              sx={{
                '& .MuiOutlinedInput-root': { height: '35px' },
                '& .MuiInputLabel-outlined': { transform: 'translate(14px, 14px) scale(1)' },
                '& .MuiInputLabel-outlined.MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.75)' }
              }}
            />
          </Grid>

          {/* Filtro por Estado de pago (servidor) */}
          <Grid item xs={6} sm={3} md={2}>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              onClick={(e) => handleFilterClick(e, 'status')}
              endIcon={<FilterListIcon fontSize="small" />}
              sx={{ height: '35px', justifyContent: 'space-between', textTransform: 'none' }}
            >
              Estado Pago
              {filters.statuses.length > 0 && (
                <Chip label={filters.statuses.length} size="small" sx={{ ml: 1 }} />
              )}
            </Button>
          </Grid>

          {/* RFC / Partner (local) */}
          {!isProveedor && (
            <>
              <Grid item xs={6} sm={3} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  onClick={(e) => handleFilterClick(e, 'rfc')}
                  endIcon={<FilterListIcon fontSize="small" />}
                  sx={{ height: '35px', justifyContent: 'space-between', textTransform: 'none' }}
                >
                  RFC Emisor
                  {filters.rfcs.length > 0 && <Chip label={filters.rfcs.length} size="small" sx={{ ml: 1 }} />}
                </Button>
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  onClick={(e) => handleFilterClick(e, 'partner')}
                  endIcon={<FilterListIcon fontSize="small" />}
                  sx={{ height: '35px', justifyContent: 'space-between', textTransform: 'none' }}
                >
                  Partner
                  {filters.partners.length > 0 && <Chip label={filters.partners.length} size="small" sx={{ ml: 1 }} />}
                </Button>
              </Grid>
            </>
          )}

          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Fecha desde"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.fechaDesde}
              onChange={(e) => { setFilters({ ...filters, fechaDesde: e.target.value }); setPage(0); }}
              sx={{ '& .MuiInputBase-root': { height: '35px' }, '& .MuiInputBase-input': { fontSize: '0.8rem', p: '8.5px 14px' } }}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Fecha hasta"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.fechaHasta}
              onChange={(e) => { setFilters({ ...filters, fechaHasta: e.target.value }); setPage(0); }}
              sx={{ '& .MuiInputBase-root': { height: '35px' }, '& .MuiInputBase-input': { fontSize: '0.8rem', p: '8.5px 14px' } }}
            />
          </Grid>

          <Grid item xs={6} sm={2} md={1}>
            <Button
              fullWidth
              size="small"
              variant="outlined"
              color="error"
              onClick={handleClearFilters}
              startIcon={<ClearIcon fontSize="small" />}
              sx={{ height: '35px', fontSize: '0.7rem' }}
            >
              Limpiar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Popover filtros */}
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { width: 300, maxHeight: 420, p: 1 } }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder={
            currentFilter === 'status' ? 'Buscar estado...' : currentFilter === 'rfc' ? 'Buscar RFC...' : 'Buscar partner...'
          }
          value={filterSearch}
          onChange={(e) => setFilterSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 1 }}
        />

        {/* Seleccionar todos */}
        {currentFilter !== 'status' ? (!isProveedor && (
          <>
            <ListItemButton onClick={handleSelectAllFilterItems}>
              <Checkbox
                edge="start"
                checked={
                  currentFilter === 'rfc'
                    ? filters.rfcs.length === getUniqueRfcsInData().length
                    : filters.partners.length === getUniquePartnersInData().length
                }
                indeterminate={
                  currentFilter === 'rfc'
                    ? filters.rfcs.length > 0 && filters.rfcs.length < getUniqueRfcsInData().length
                    : filters.partners.length > 0 && filters.partners.length < getUniquePartnersInData().length
                }
                tabIndex={-1}
                disableRipple
              />
              Seleccionar todos
            </ListItemButton>
            <Divider />
          </>
        )) : (
          <>
            <ListItemButton onClick={handleSelectAllFilterItems}>
              <Checkbox
                edge="start"
                checked={filters.statuses.length === STATUS_OPTIONS.length}
                indeterminate={filters.statuses.length > 0 && filters.statuses.length < STATUS_OPTIONS.length}
                tabIndex={-1}
                disableRipple
              />
              Seleccionar todos
            </ListItemButton>
            <Divider />
          </>
        )}

        <List dense sx={{ width: '100%', maxHeight: 320, overflow: 'auto' }}>
          {getFilteredOptions().map((value) => {
            const key = String(value);
            const selected =
              currentFilter === 'rfc'
                ? filters.rfcs.includes(key)
                : currentFilter === 'partner'
                  ? filters.partners.includes(key)
                  : filters.statuses.includes(key);

            return (
              <ListItem key={key} disablePadding>
                <ListItemButton onClick={() => handleToggleFilterItem(key)} dense>
                  <Checkbox edge="start" checked={selected} tabIndex={-1} disableRipple />
                  <Typography variant="body2">
                    {key}
                    {currentFilter === 'partner' && (
                      <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                        {getUserNameByPartner(key)}
                      </Typography>
                    )}
                  </Typography>
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Popover>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : rowsToShow.length === 0 ? (
        <Paper elevation={1} sx={{ p: 2, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <Typography variant="body2">
            {searchTerm || Object.values(filters).some(v => Array.isArray(v) ? v.length > 0 : !!v)
              ? 'No se encontraron resultados'
              : 'No hay registros disponibles'}
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer
            component={Paper}
            elevation={1}
            sx={{
              maxHeight: '560px',
              overflow: 'auto',
              '&::-webkit-scrollbar': { width: '6px', height: '6px' },
              '&::-webkit-scrollbar-thumb': { backgroundColor: '#bdbdbd', borderRadius: '3px' }
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{
                  backgroundColor: '#f3f4f6',
                  '& th': { fontWeight: 'bold', fontSize: '0.75rem', py: 1, borderBottom: '1px solid #bdbdbd' }
                }}>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'doc'}
                      direction={orderBy === 'doc' ? order : 'asc'}
                      onClick={() => handleRequestSort('doc')}
                    >
                      Doc (Soc/Año/Núm)
                    </TableSortLabel>
                  </TableCell>

                  <TableCell>Referencia Proveedor</TableCell>

                  {/* Estado de factura (NUEVA COLUMNA) */}
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'InvoiceDocStatus'}
                      direction={orderBy === 'InvoiceDocStatus' ? order : 'asc'}
                      onClick={() => handleRequestSort('InvoiceDocStatus')}
                    >
                      Estado Factura
                    </TableSortLabel>
                  </TableCell>

                  {/* Estado de pago (renombrado) */}
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'Status'}
                      direction={orderBy === 'Status' ? order : 'asc'}
                      onClick={() => handleRequestSort('Status')}
                    >
                      Estado Pago
                    </TableSortLabel>
                  </TableCell>

                  {!isMobile && (
                    <>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'ValueDate'}
                          direction={orderBy === 'ValueDate' ? order : 'asc'}
                          onClick={() => handleRequestSort('ValueDate')}
                        >
                          Fecha Valor
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'EstimatedDueDate'}
                          direction={orderBy === 'EstimatedDueDate' ? order : 'asc'}
                          onClick={() => handleRequestSort('EstimatedDueDate')}
                        >
                          Vencimiento Est.
                        </TableSortLabel>
                      </TableCell>
                    </>
                  )}

                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'PostingDate'}
                      direction={orderBy === 'PostingDate' ? order : 'asc'}
                      onClick={() => handleRequestSort('PostingDate')}
                    >
                      Fecha Contab.
                    </TableSortLabel>
                  </TableCell>

                  {!isMobile && (
                    <>
                      <TableCell align="right">
                        <TableSortLabel
                          active={orderBy === 'InvoiceGrossAmount'}
                          direction={orderBy === 'InvoiceGrossAmount' ? order : 'asc'}
                          onClick={() => handleRequestSort('InvoiceGrossAmount')}
                        >
                          Importe
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Mon.</TableCell>
                      <TableCell>Partner</TableCell>
                      <TableCell>RFC</TableCell>
                    </>
                  )}

                  {/* Doc financiero (renombrado) */}
                  <TableCell>Doc. Financiero</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rowsToShow.map((row) => {
                  const docKey = `${row.CompanyCode}-${row.FiscalYear}-${row.SupplierInvoice}`;
                  const fecha = (val) => val
                    ? new Date(val).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })
                    : '-';

                  return (
                    <TableRow key={`${docKey}`} hover sx={{
                      '&:hover': { backgroundColor: '#f5f5f5' },
                      '& td': { fontSize: '0.75rem', py: 0.6 }
                    }}>
                      <TableCell>{docKey}</TableCell>

                      <TableCell>
                        {row.SupplierInvoiceRef
                          ? <Chip label={row.SupplierInvoiceRef} size="small" sx={{ fontSize: '0.7rem', height: '22px' }} />
                          : <Typography variant="caption" color="text.secondary">—</Typography>}
                      </TableCell>

                      {/* Estado de factura */}
                      <TableCell>
                        <Chip
                          label={row.InvoiceDocStatus || '—'}
                          size="small"
                          color={docStatusColor(row.InvoiceDocStatus)}
                          sx={{ fontSize: '0.7rem', height: '22px' }}
                        />
                      </TableCell>

                      {/* Estado de pago */}
                      <TableCell>
                        <Chip
                          label={row.Status || '—'}
                          size="small"
                          color={statusColor(row.Status)}
                          sx={{ fontSize: '0.7rem', height: '22px' }}
                        />
                      </TableCell>

                      {!isMobile && (
                        <>
                          <TableCell>{fecha(row.ValueDate)}</TableCell>
                          <TableCell>{fecha(row.EstimatedDueDate)}</TableCell>
                        </>
                      )}

                      <TableCell>{fecha(row.PostingDate)}</TableCell>

                      {!isMobile && (
                        <>
                          <TableCell align="right">
                            {row.InvoiceGrossAmount != null
                              ? Number(row.InvoiceGrossAmount).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                              : '—'}
                          </TableCell>
                          <TableCell>{row.DocumentCurrency || '-'}</TableCell>
                          <TableCell>{row.Supplier || '-'}</TableCell>
                          <TableCell>{getRfcByPartner(row.Supplier) || '-'}</TableCell>
                        </>
                      )}

                      {/* Doc financiero */}
                      <TableCell>
                        {row.PaymentDocument ? (
                          <Chip
                            label={row.PaymentDocument}
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: '22px' }}
                          />
                        ) : <Typography variant="caption" color="text.secondary">—</Typography>}
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => openDetail(row)}
                            title="Ver detalle"
                            color="primary"
                            sx={{ p: 0.5, '&:hover': { backgroundColor: 'rgba(25,118,210,0.08)' } }}
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => window.open(`https://facturas.verdevalle.com.mx/api/portal/invoices/${row.CompanyCode}/${row.FiscalYear}/${row.SupplierInvoice}`, '_blank')}
                            title="Abrir JSON"
                            sx={{ p: 0.5 }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100, 250, 500]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{
              mt: 1,
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem' }
            }}
          />
        </>
      )}

      {/* Diálogo de detalle */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Detalle del documento</DialogTitle>
        <DialogContent dividers>
          {!detail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : detail.error ? (
            <Alert severity="error" sx={{ my: 1 }}>{detail.error}</Alert>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Doc:</strong> {detail?.header?.CompanyCode}-{detail?.header?.FiscalYear}-{detail?.header?.SupplierInvoice}
              </Typography>
              <Typography variant="body2"><strong>Referencia:</strong> {detail?.header?.SupplierInvoiceRef || '—'}</Typography>
              <Typography variant="body2"><strong>Estado Factura:</strong> {detail?.header?.InvoiceDocStatus || '—'}</Typography>
              <Typography variant="body2"><strong>Estado Pago:</strong> {detail?.header?.Status || '—'}</Typography>
              <Typography variant="body2"><strong>Motivo Exclusión:</strong> {detail?.header?.ExclusionReason || '—'}</Typography>
              <Typography variant="body2"><strong>Contab.:</strong> {detail?.header?.PostingDate ? new Date(detail.header.PostingDate).toLocaleDateString('es-MX') : '—'}</Typography>
              <Typography variant="body2"><strong>Fecha valor:</strong> {detail?.header?.ValueDate ? new Date(detail.header.ValueDate).toLocaleDateString('es-MX') : '—'}</Typography>
              <Typography variant="body2"><strong>Venc. Est.:</strong> {detail?.header?.EstimatedDueDate ? new Date(detail.header.EstimatedDueDate).toLocaleDateString('es-MX') : '—'}</Typography>
              <Typography variant="body2"><strong>Doc. Financiero:</strong> {detail?.header?.PaymentDocument || '—'}</Typography>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="body2"><strong>Importe:</strong> {detail?.header?.InvoiceGrossAmount != null ? Number(detail.header.InvoiceGrossAmount).toLocaleString('es-MX', { minimumFractionDigits: 2 }) : '—'} {detail?.header?.DocumentCurrency || ''}</Typography>
              <Typography variant="body2"><strong>Partner:</strong> {detail?.header?.Supplier || '—'} ({getRfcByPartner(detail?.header?.Supplier)})
              </Typography>

              {detail?.paymentLine && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Propuesta / Pago</Typography>
                  <Typography variant="body2"><strong>Documento pago:</strong> {detail.paymentLine.PaymentDocument || '—'}</Typography>
                  <Typography variant="body2"><strong>Fecha pago:</strong> {detail.paymentLine.PaymentPostingDate ? new Date(detail.paymentLine.PaymentPostingDate).toLocaleDateString('es-MX') : '—'}</Typography>
                  <Typography variant="body2"><strong>Fecha valor:</strong> {detail.paymentLine.ValueDate ? new Date(detail.paymentLine.ValueDate).toLocaleDateString('es-MX') : '—'}</Typography>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%', fontSize: '0.8rem' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PagosProv;
